<template>
    <section class="mention-conversation-container" :class="{'conversation--direct-message': directMessage}" aria-label="Conversation">
        <conversation-toolbar :author-name="selectedMention.authorName" :ready="!!filter"
                              @close="onClose()" @focus="highlightSelected" @mentions="seeMentions" @authors="seeAuthors"/>
        <div ref="scroll" class="conversation-scroll">
            <conversation-info v-if="filter" :key="filter" :filter="filter"
                               :conversation-id="mentionLoader.conversationId" :account-code="accountCode"
                               :direct-message="directMessage" :date-range="mentionLoader.dateRange"/>
            <loading-message v-if="loading" message="Loading conversation…" role="status"/>
            <error-message v-if="tooLarge" class="conversation-size-error" role="alert">
                <h1>This conversation is too large to display</h1>
                <p v-if="directMessage">Conversation view supports up to {{ conversationMentionLimit }} direct messages in the date range.</p>
                <p v-else>Conversation view supports up to {{ conversationMentionLimit }} public, relevant mentions, excluding reshares.</p>
            </error-message>
            <p v-else-if="error" class="conversation-message" role="alert">
                {{ errorMessage }} <button class="btn" :disabled="loading" @click="fetchData">Try again</button>
            </p>
            <div v-show="!tooLarge" ref="thread" class="conversation-thread">
                <svg v-if="!directMessage" ref="branches" class="conversation-branches" aria-hidden="true"/>
                <div v-for="row in rows" :key="row.id" :data-row-id="row.id"
                     class="conversation-row" :class="{'conversation-row--control': row.kind === 'earlier' || row.kind === 'later'}"
                     :style="{marginLeft: indent(row) + 'px'}">
                    <template v-if="row.kind === 'earlier' || row.kind === 'later'">
                        <inline-loading-message v-if="row.checking" class="conversation-placeholder" role="status">
                            Checking for {{ row.kind }} messages…
                        </inline-loading-message>
                        <div v-else-if="row.checkError" class="conversation-placeholder" role="alert">
                            Unable to check for {{ row.kind }} messages.
                            <button class="btn" :disabled="busy || loading" @click="expand(row)">Try again</button>
                        </div>
                        <button v-else class="btn conversation-placeholder" :disabled="busy || loading" @click="expand(row)">
                            <inline-loading-message v-if="expandingId === row.id" role="status">Loading messages…</inline-loading-message>
                            <template v-else>
                                {{ row.kind === 'earlier' ? 'Earlier messages' : 'Later messages' }}
                                <span v-if="row.extendDateRange"> — extend date range</span>
                                <span v-else> ({{ formatNumber(row.count) }})</span>
                            </template>
                        </button>
                    </template>
                    <template v-else-if="row.kind === 'more' || row.kind === 'gap'">
                        <button class="btn conversation-placeholder" :disabled="busy || loading" @click="expand(row)">
                            <inline-loading-message v-if="expandingId === row.id" role="status">Loading mentions…</inline-loading-message>
                            <template v-else>
                                {{ row.kind === 'more' ? 'More replies' : 'Show intervening mentions' }}
                                <span v-if="row.count"> ({{ formatNumber(row.count) }})</span>
                            </template>
                        </button>
                    </template>
                    <template v-else>
                        <div :data-mention-id="row.id" :class="{'conversation-selected': row.id === selectedMention.id}">
                            <mention-item v-if="row.mention" :mention="row.mention" :options="mentionOptions(row.mention)" @change="onMentionChange"/>
                            <div v-else class="conversation-placeholder">This mention is unavailable.</div>
                        </div>
                        <div v-if="row.loadingReshares || reshareErrors[row.id]" class="conversation-reshare-status">
                            <inline-loading-message v-if="row.loadingReshares" role="status">Loading reshares…</inline-loading-message>
                            <div v-if="reshareErrors[row.id]" role="alert">
                                Unable to load reshares.
                                <button class="btn" @click="loadReshareRow(mentionLoader, row)">Try again</button>
                            </div>
                        </div>
                        <div v-if="row.reshares && row.reshares.length" class="conversation-reshares">
                            <span class="conversation-reshares__label">Reshares ▸</span>
                            <div v-for="mention in resharePreviews(row)" :key="mention.id" :data-mention-id="mention.id"
                                 :class="{'conversation-selected': mention.id === selectedMention.id}">
                                <mention-item :mention="mention" :options="{authorOnly: true, noSelect: true, noViewConversation: true}" @change="onMentionChange"/>
                            </div>
                            <button v-if="moreReshareCount(row)" class="conversation-more-reshares" @click="seeReshares(row)">
                                {{ formatNumber(moreReshareCount(row)) }} more<br>reshares
                            </button>
                        </div>
                    </template>
                </div>
            </div>
            <p v-if="actionError" class="conversation-message" role="alert">{{ actionError }}</p>
        </div>
    </section>
</template>

<script>
import {select} from 'd3';
import {ConversationMotion} from '@/conversation/utils/ConversationMotion';
import ConversationInfo from '@/conversation/ConversationInfo';
import ConversationToolbar from '@/conversation/ConversationToolbar';
import {MentionConversationLoader, CONVERSATION_TOO_LARGE} from '@/conversation/utils/MentionConversationLoader';
import {getReshareFilter, CONVERSATION_MENTION_LIMIT} from '@/conversation/utils/GrouseConversationProxy';
import {DirectMessageConversationLoader} from '@/conversation/utils/DirectMessageConversationLoader';
import {isDirectMessage} from '@/conversation/utils/DirectMessageConversationProxy';
import {account} from '@/app/utils/Account';
import ErrorMessage from '@/components/ErrorMessage';
import MentionItem from '@/components/MentionItem.vue';
import LoadingMessage from '@/components/LoadingMessage';
import InlineLoadingMessage from '@/components/InlineLoadingMessage';
import {formatNumber} from '@/app/utils/Format';
import {getGreenScheme} from '@/app/utils/Colours';

export default {
    name: 'MentionConversationView',
    components: {MentionItem, ConversationToolbar, ConversationInfo, LoadingMessage, InlineLoadingMessage, ErrorMessage},
    props: {
        selectedMention: {type: Object, required: true},
        accountCode: {type: String, required: true},
        onClose: {type: Function, required: true},
        onMentionChange: {type: Function, default: () => {}}
    },
    data() {
        return {mentionLoader: null, busy: false, expandingId: null, actionError: null, reshareErrors: {}, narrow: false};
    },
    computed: {
        directMessage() { return this.selectedMention.isDirectMessage || isDirectMessage(this.selectedMention); },
        loading() { return this.mentionLoader?.loading ?? true; },
        error() { return this.mentionLoader?.error; },
        tooLarge() { return this.error?.code === CONVERSATION_TOO_LARGE; },
        conversationMentionLimit() { return formatNumber(CONVERSATION_MENTION_LIMIT); },
        errorMessage() { return this.error?.message || this.error || 'Unable to load this conversation.'; },
        filter() { return this.mentionLoader?.filter || ''; },
        rows() { return this.mentionLoader?.visibleRows() ?? []; }
    },
    watch: {
        selectedMention: 'fetchData',
        accountCode: 'fetchData',
        rows() { this.$nextTick(this.observeRows); }
    },
    mounted() {
        this.alive = true;
        this.motion = new ConversationMotion();
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.motionPreference.addEventListener('change', this.scheduleLayout);
        this.resizeObserver = new ResizeObserver(this.resize);
        this.resizeObserver.observe(this.$refs.thread);
        window.addEventListener('resize', this.resize);
        this.resize();
        this.fetchData();
    },
    updated() { this.layout(); },
    beforeDestroy() {
        this.alive = false;
        this.motion?.dispose();
        this.motionPreference?.removeEventListener('change', this.scheduleLayout);
        cancelAnimationFrame(this.branchFrame);
        this.mentionLoader?.dispose();
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.layoutFrame);
        window.removeEventListener('resize', this.resize);
    },
    methods: {
        formatNumber,
        indent(row) { return Math.min(row.depth || 0, 12) * 20; },
        mentionOptions(mention) {
            return {
                noSelect: true, noViewConversation: true, includePhoto: true,
                includeExportButton: true, isConversationView: true,
                usePublishFull: !!mention.publishFull
            };
        },
        resharePreviews(row) {
            const previews = row.reshares.slice(0, this.narrow ? 1 : 2);
            const selected = row.reshares.find(mention => mention.id === this.selectedMention.id);
            if (selected && !previews.includes(selected)) previews[previews.length - 1] = selected;
            return previews;
        },
        moreReshareCount(row) {
            const relevantPreviews = this.resharePreviews(row).filter(mention => mention.relevancy !== 'IRRELEVANT');
            return Math.max(0, (row.reshareTotal || 0) - relevantPreviews.length);
        },
        async fetchData() {
            this.mentionLoader?.dispose();
            const loader = this.directMessage
                ? new DirectMessageConversationLoader(this.accountCode, this.selectedMention, account()?.timezone)
                : new MentionConversationLoader(this.accountCode, this.selectedMention);
            this.mentionLoader = loader;
            this.actionError = null;
            this.reshareErrors = {};
            this.busy = false;
            this.expandingId = null;
            await loader.fetchData();
            if (!this.alive || loader !== this.mentionLoader) return;
            await this.$nextTick();
            this.observeRows();
            if (this.directMessage) {
                this.highlightSelected(false);
                return;
            }
            const focusReshare = this.selectedMention.id !== loader.conversationId && !this.highlightSelected(false);
            await this.loadReshares(loader);
            // A selected reshare may not have been mounted with the initial replies.
            if (this.alive && loader === this.mentionLoader && focusReshare) {
                await this.$nextTick();
                this.highlightSelected(false);
            }
        },
        async expand(row) {
            if (this.busy) return;
            const loader = this.mentionLoader;
            this.busy = true;
            this.expandingId = row.id;
            this.actionError = null;
            try {
                await loader.expand(row);
                if (!this.alive || loader !== this.mentionLoader) return;
                await this.$nextTick();
                this.observeRows();
                await this.loadReshares(loader);
            } catch (e) {
                if (this.alive && loader === this.mentionLoader) {
                    this.actionError = this.directMessage
                        ? (e.code === CONVERSATION_TOO_LARGE ? e.message : 'Unable to load more messages. Please try again.')
                        : 'Unable to load more replies. Please try again.';
                }
            } finally {
                if (this.alive && loader === this.mentionLoader) {
                    this.busy = false;
                    this.expandingId = null;
                }
            }
        },
        async loadReshares(loader) {
            if (this.directMessage) return;
            for (const row of loader.visibleRows()) {
                if (!this.alive || loader !== this.mentionLoader) return;
                if (row.kind !== 'mention' || !row.mention) continue;
                await this.loadReshareRow(loader, row);
            }
            if (this.alive && loader === this.mentionLoader) this.$nextTick(this.observeRows);
        },
        async loadReshareRow(loader, row) {
            if (!this.alive || loader !== this.mentionLoader) return;
            this.$delete(this.reshareErrors, row.id);
            try {
                await loader.loadReshares(row);
            } catch (e) {
                if (this.alive && loader === this.mentionLoader) this.$set(this.reshareErrors, row.id, true);
            }
        },
        seeMentions() {
            const filter = this.filter;
            this.onClose();
            Beef.MentionList.navigateToMentions(this.accountCode, filter, 'published');
        },
        seeAuthors() {
            const filter = this.filter;
            this.onClose();
            Beef.AuthorsSectionV4.navigateToAuthors(this.accountCode, filter);
        },
        seeReshares(row) {
            const filter = getReshareFilter(row.id);
            this.onClose();
            Beef.MentionList.navigateToMentions(this.accountCode, filter, 'published');
        },
        highlightSelected(smooth = true) {
            const element = Array.from(this.$el.querySelectorAll('[data-mention-id]'))
                .find(element => element.dataset.mentionId === this.selectedMention.id);
            if (!element) return;
            if (this.directMessage) {
                element.scrollIntoView({
                    block: 'start', inline: 'nearest', container: 'nearest',
                    behavior: smooth && !this.motionPreference.matches ? 'smooth' : 'auto'
                });
                return true;
            }
            this.motion.finish();
            cancelAnimationFrame(this.branchFrame);
            this.drawBranches();
            // Scroll only the conversation, not the underlying Mentions panel.
            const scroll = this.$refs.scroll;
            const bounds = element.getBoundingClientRect();
            const viewport = scroll.getBoundingClientRect();
            const horizontal = bounds.left < viewport.left + 24 ? bounds.left - viewport.left - 24
                : bounds.right > viewport.right - 24 ? bounds.right - viewport.right + 24 : 0;
            scroll.scrollTo({
                top: scroll.scrollTop + bounds.top - viewport.top - 24,
                left: scroll.scrollLeft + horizontal,
                behavior: smooth && !this.motionPreference.matches ? 'smooth' : 'auto'
            });
            return true;
        },
        resize() { this.narrow = this.$el.clientWidth <= 1024; this.layout(); },
        observeRows() {
            if (!this.alive) return;
            this.resizeObserver.disconnect();
            this.resizeObserver.observe(this.$refs.thread);
            this.$el.querySelectorAll('.conversation-row').forEach(element => this.resizeObserver.observe(element));
            this.scheduleLayout();
        },
        scheduleLayout() {
            cancelAnimationFrame(this.layoutFrame);
            this.layoutFrame = requestAnimationFrame(this.layout);
        },
        layout() {
            // DM rows use native scroll anchoring. Movement transforms can suppress that anchoring.
            if (!this.alive || this.directMessage) return;
            // Vue updates and ResizeObserver callbacks run before paint. Apply the
            // inverse displacement here, not a frame later (which would flash the
            // destination before moving back to animate towards it).
            this.motion.update(Array.from(this.$refs.thread.querySelectorAll(
                '.conversation-row, .conversation-reshares > [data-mention-id], .conversation-more-reshares'
            )), this.motionPreference.matches);
            cancelAnimationFrame(this.branchFrame);
            this.drawBranches();
        },
        drawBranches() {
            if (!this.alive || this.directMessage) return;
            const thread = this.$refs.thread;
            // Read the animated bounds, not layout offsets: branches must stay
            // attached throughout movement, including image-driven height changes.
            const origin = thread.getBoundingClientRect();
            const elements = new Map(Array.from(thread.querySelectorAll('[data-row-id]')).map(el => [
                el.dataset.rowId, el.firstElementChild.getBoundingClientRect()
            ]));
            const links = [];
            for (const row of this.rows) {
                const child = elements.get(row.id);
                const parent = elements.get(row.parentId);
                if (!child || !parent) continue;
                const x = child.left - origin.left - 10;
                links.push({id: row.id, depth: row.depth, path: `M${parent.left - origin.left + 5},${parent.bottom - origin.top} H${x} V${child.top - origin.top + 20} H${child.left - origin.left}`});
            }
            const colours = getGreenScheme();
            // Use the normal-flow bounds: scrollHeight includes the SVG's previous size,
            // which would otherwise prevent the thread from shrinking after a retry or resize.
            const svg = select(this.$refs.branches).attr('width', thread.offsetWidth).attr('height', thread.offsetHeight);
            const paths = svg.selectAll('path').data(links, link => link.id);
            paths.exit().remove();
            paths.enter().append('path').merge(paths)
                .attr('d', link => link.path).attr('fill', 'none')
                .attr('stroke', link => colours[(link.depth || 0) % colours.length]).attr('stroke-width', 8)
                .attr('stroke-linejoin', 'round');
            // The reshare bar follows the visible reshare previews and More reshares
            // button, rather than snapping to its final width when the preview count changes.
            thread.querySelectorAll('.conversation-reshares').forEach(group => {
                const bounds = group.getBoundingClientRect();
                const tiles = Array.from(group.querySelectorAll('[data-mention-id], .conversation-more-reshares'));
                const right = tiles.length ? Math.max(...tiles.map(tile => tile.getBoundingClientRect().right)) : bounds.right;
                group.style.setProperty('--reshare-bar-width', `${right - bounds.left + 90}px`);
            });
            if (this.motion.active) this.branchFrame = requestAnimationFrame(this.drawBranches);
        }
    }
};
</script>

<style scoped>
.mention-conversation-container { position: fixed; inset: 40px 0 0; display: flex; flex-direction: column; background: var(--body-background-colour); }
.conversation-scroll { flex: 1; min-height: 0; overflow: auto; }
/* Anchor to mention cards, not controls or statistics which can change while requests finish.
   We intentionally wait for Safari to support native scroll anchoring instead of adding a JavaScript fallback. */
.conversation--direct-message .conversation-row--control,
.conversation--direct-message .conversation-info { overflow-anchor: none; }
.conversation--direct-message [data-mention-id] { scroll-margin: 24px; }
.conversation-thread { position: relative; padding: 24px 72px 48px; width: max-content; min-width: calc(100% - 144px); }
.conversation-row { position: relative; display: flex; align-items: flex-start; gap: 84px; margin-bottom: 12px; width: max-content; }
.conversation-branches { position: absolute; top: 0; left: 0; pointer-events: none; }
.conversation-selected { outline: 3px solid #85b03d; outline-offset: 4px; border-radius: 2px; }
.conversation-placeholder { padding: 16px; background: var(--body-background-colour); border: 1px solid #666; border-radius: 3px; min-width: 240px; }
.conversation-reshare-status { margin-top: 52px; }
.conversation-reshares { display: flex; align-items: flex-start; gap: 10px; position: relative; margin-top: 52px; min-height: 66px; }
.conversation-reshares::before {
    content: ''; position: absolute; left: -84px; width: var(--reshare-bar-width, calc(100% + 90px)); top: 58px; height: 15px;
    background: #489522; border: 1px solid #36751b; border-radius: 2px;
    transform-origin: left; animation: conversation-bar-in 800ms ease-out;
}
.conversation-reshares > [data-mention-id] { position: relative; }
.conversation-reshares__label { position: absolute; left: -80px; top: 40px; font-size: 12px; white-space: nowrap; }
.conversation-more-reshares {
    position: relative; width: 96px; min-height: 66px; padding: 8px; border: 0; border-radius: 3px;
    background: #f8f8f8 url('../img/chart-background.png'); color: #000; font: inherit;
    line-height: 1.2; cursor: pointer; box-shadow: 0 3px 10px #0009;
}
.conversation-more-reshares:hover { background-color: #e5e5e5; }
.conversation-more-reshares:focus-visible { outline: 2px solid #85b03d; outline-offset: 4px; }
@keyframes conversation-bar-in { from { transform: scaleX(0); opacity: 0; } to { transform: scaleX(1); opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .conversation-reshares::before { animation: none; } }
@media (max-width: 1024px) {
    .conversation-thread { padding-left: 32px; padding-right: 32px; min-width: calc(100% - 64px); }
}
.conversation-message { margin: 16px 24px; }
.conversation-size-error { min-height: 0; padding: 32px 24px; }
.conversation-row ::v-deep .mention-item,
.conversation-row ::v-deep .mention-item-author-only { margin: 0; float: none; }
.conversation-row ::v-deep .mention-item:not(.irrelevant),
.conversation-row ::v-deep .mention-item-author-only:not(.irrelevant) { box-shadow: 0 3px 10px #0009; }
.conversation-row ::v-deep .mention-item { height: auto; min-height: 169px; }
.conversation-row ::v-deep .mention-item .content { height: auto; min-height: 76px; }
.conversation-row ::v-deep iframe.facebook-video {
    display: block;
    max-width: 100%;
    height: fit-content;
    aspect-ratio: 5 / 3;
}
</style>
